import { useTranslation } from 'react-i18next'

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  className?: string
  showInfo?: boolean
  totalItems?: number
  pageSize?: number
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  className = '',
  showInfo = true,
  totalItems,
  pageSize,
}: PaginationProps) {
  const { t } = useTranslation()

  const getPageNumbers = () => {
    const delta = 2 // Number of pages to show on each side of current page
    const range: number[] = []
    const rangeWithDots: (number | string)[] = []
    let l: number | undefined

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        range.push(i)
      }
    }

    range.forEach((i) => {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1)
        } else if (i - l !== 1) {
          rangeWithDots.push('...')
        }
      }
      rangeWithDots.push(i)
      l = i
    })

    return rangeWithDots
  }

  if (totalPages <= 1) return null

  const pages = getPageNumbers()
  const startItem = (currentPage - 1) * (pageSize || 0) + 1
  const endItem = Math.min(currentPage * (pageSize || 0), totalItems || 0)

  return (
    <div className={`flex items-center justify-between ${className}`}>
      {showInfo && totalItems && pageSize && (
        <div className="text-sm text-foreground">
          {t('common.showingResults', { start: startItem, end: endItem, total: totalItems })}
        </div>
      )}

      <div className="flex items-center gap-2">
        {/* Previous button */}
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-muted-foreground bg-card border border-input rounded-md hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={t('common.previousPage')}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        {/* Page numbers */}
        {pages.map((page, index) => {
          if (page === '...') {
            return (
              <span
                key={`dots-${index}-${page}`}
                className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-foreground bg-card border border-input"
              >
                ...
              </span>
            )
          }

          const pageNumber = page as number
          const isActive = pageNumber === currentPage

          return (
            <button
              key={pageNumber}
              type="button"
              onClick={() => onPageChange(pageNumber)}
              className={`relative inline-flex items-center px-4 py-2 text-sm font-medium border ${
                isActive
                  ? 'z-10 bg-primary text-white border-primary'
                  : 'bg-card text-foreground border-input hover:bg-muted'
              } rounded-md`}
              aria-current={isActive ? 'page' : undefined}
            >
              {pageNumber}
            </button>
          )
        })}

        {/* Next button */}
        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-muted-foreground bg-card border border-input rounded-md hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={t('common.nextPage')}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  )
}
